import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Panel, SectionHead, SectionShell } from "./_shared";
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";

// Maps ops-dashboard cards -> actionable "Needs You" items (reuses existing data; no duplicated logic)
const SPEC = {
  outstanding_invoices: { sev: "high", action: "Collect", to: "/invoices" },
  overdue_followups: { sev: "high", action: "Follow up", to: "/follow-ups" },
  overdue_returns: { sev: "high", action: "Reach out", to: "/action-center" },
  vet_followups_due: { sev: "med", action: "Review", to: "/follow-ups" },
  memberships_expiring: { sev: "med", action: "Renew", to: "/action-center" },
  pending_followups: { sev: "med", action: "Follow up", to: "/follow-ups" },
  customers_due_return: { sev: "low", action: "View", to: "/action-center" },
};
const ORDER = ["outstanding_invoices", "overdue_followups", "overdue_returns", "vet_followups_due",
  "memberships_expiring", "pending_followups", "customers_due_return"];
const SEV_DOT = { high: "bg-pawkt-emergency", med: "bg-pawkt-warning", low: "bg-pawkt-info" };

export function NeedsYou({ canReports }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["ops-dashboard"], enabled: canReports,
    queryFn: async () => (await api.get("/ops-dashboard")).data,
  });
  if (!canReports) return null;

  return (
    <Panel className="p-5 sm:p-6" data-testid="needs-you">
      <SectionHead eyebrow="Needs You" title="What needs your attention"
        hint="Live exceptions from across your business." />
      <SectionShell query={q}>
        {(d) => {
          const cards = d.cards || {};
          const items = ORDER.filter((k) => cards[k] && (cards[k].value || 0) > 0 && SPEC[k]);
          if (items.length === 0) {
            return (
              <div className="flex items-center gap-3 rounded-2xl bg-pawkt-mint/60 border border-pawkt-border-soft px-4 py-5" data-testid="needs-you-clear">
                <CheckCircle2 className="h-6 w-6 text-pawkt-success-deep" />
                <div>
                  <p className="text-sm font-bold text-pawkt-text">You're all caught up.</p>
                  <p className="text-xs text-pawkt-text-secondary">No outstanding actions right now — nicely done.</p>
                </div>
              </div>
            );
          }
          return (
            <div className="grid gap-2.5 sm:grid-cols-2">
              {items.map((k) => {
                const c = cards[k]; const s = SPEC[k];
                return (
                  <button key={k} data-testid={`needs-${k}`} onClick={() => navigate(s.to)}
                    className="group flex items-center gap-3 rounded-2xl border border-pawkt-border-soft bg-white px-4 py-3 text-left hover:border-pawkt-border-strong hover:shadow-card transition-all">
                    <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", SEV_DOT[s.sev])} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-display text-lg font-extrabold text-pawkt-text tabular">
                          {c.currency ? inr(c.value) : c.value}
                        </span>
                        <span className="truncate text-xs text-pawkt-text-secondary">{c.label}</span>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 rounded-full bg-pawkt-primary-soft px-2.5 py-1 text-[11px] font-bold text-pawkt-primary-deep group-hover:bg-pawkt-primary group-hover:text-white transition-colors">
                      {s.action} <ArrowRight className="h-3 w-3" />
                    </span>
                  </button>
                );
              })}
            </div>
          );
        }}
      </SectionShell>
    </Panel>
  );
}
